import { JsonPipe, KeyValuePipe } from '@angular/common'
import { Component, inject, OnInit, signal } from '@angular/core'
import { MatChipListbox, MatChipOption } from '@angular/material/chips'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatTab, MatTabGroup } from '@angular/material/tabs'
import { ActivatedRoute } from '@angular/router'
import {
  DataContextBuilder,
  ElderExceptionDetailComponent,
  ElderHeaderComponent,
  ElderTableComponent,
  ElderToolbarColumnDirective,
  IDataContext,
  IsoDurationPipe,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { Job } from '../job'
import { JobService } from '../job.service'
import { ProductionLine } from '../production-line'
import { WorkItem } from '../work-item'
import { JobDetailConvergenceComponent } from './job-detail-convergence/job-detail-convergence.component'
import { JobDetailProductionLineViewComponent } from './job-detail-production-line-view/job-detail-production-line-view.component'
import { JobDetailUtilisationComponent } from './job-detail-utilisation/job-detail-utilisation.component'

@Component({
  selector: 'app-job-detail',
  templateUrl: './job-detail.component.html',
  styleUrls: ['./job-detail.component.scss'],
  imports: [
    ElderToolbarColumnDirective,
    ElderHeaderComponent,
    MatTabGroup,
    MatTab,
    ElderTableComponent,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    JobDetailProductionLineViewComponent,
    JobDetailUtilisationComponent,
    MatChipListbox,
    MatChipOption,
    ElderExceptionDetailComponent,
    JobDetailConvergenceComponent,
    JsonPipe,
    KeyValuePipe,
    ElderTranslatePipe,
    IsoDurationPipe,
  ],
})
export class JobDetailComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly logger = LoggerFactory.getLogger(this.constructor.name)

  private route = inject(ActivatedRoute)
  private jobService = inject(JobService)
  public readonly job = signal<Job | null>(null)
  public dataContext: IDataContext<WorkItem> | null = null

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    const id = this.route.snapshot.params['id']

    this.jobService.findById(id).subscribe({
      next: (job) => {
        this.logger.info('Successfully loaded job: ', job)
        this.job.set(job)

        if (job?.initialAssignments?.workItems) {
          this.dataContext = DataContextBuilder.start<WorkItem>()
            .pageSize(50)
            .localSort()
            .buildLocalActivePaged(job.initialAssignments.workItems)
          this.dataContext.start()
        }
      },
      error: (err) => this.logger.error('Failed to load job ' + id, err),
    })
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public get enforcedAssignmentFilterFn(): (workItem: WorkItem) => boolean {
    return (workItem) =>
      workItem.possibleProductionLines.length === 1 ||
      this.prodLinesContainSameWc(workItem.possibleProductionLines)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private prodLinesContainSameWc(productionLines: ProductionLine[]): boolean {
    // Requirements: WorkCenter ID is unique, WorkCenter can only appear once in a ProductionLine
    const wcCount = new Map<string, number>()
    productionLines.forEach((pl) =>
      pl.workCenters.forEach((wc) => {
        const count = wcCount.get(wc.id)
        wcCount.set(wc.id, count ? count + 1 : 1)
      })
    )

    let result = false
    wcCount.forEach((value: number) => {
      if (productionLines.length === value) {
        result = true
      }
    })
    return result
  }
}
